/**
 * Régime de TVA applicable à ce que Propelsia facture AU pro (abonnement, addons, domaine).
 * Rien à voir avec la TVA que le pro applique à SES propres clients (`pro_payment.vat_percentage`).
 *
 * Pourquoi un régime plutôt qu'un simple taux : pour une même adresse, deux traitements légaux
 * peuvent coexister (un pro guadeloupéen peut relever de la TVA locale à 8,5 % OU de
 * l'autoliquidation par le preneur selon sa situation). Aucune règle automatique ne peut trancher
 * à partir du pays seul : le régime est donc une DÉCISION, prise en admin, et le pays ne sert qu'à
 * proposer un candidat.
 *
 * Le régime porte à la fois le taux ET la mention légale à imprimer sur la facture : impossible
 * d'appliquer un taux d'exonération en oubliant la mention qui la justifie.
 */
export enum ProVatRegime {
  /** Aucune décision prise : le taux reste gelé sur la valeur en base et l'émission est bloquée. */
  UNDETERMINED = 'UNDETERMINED',
  /** France métropolitaine : TVA française au taux normal. */
  FR_METROPOLE = 'FR_METROPOLE',
  /** Guadeloupe / Martinique / La Réunion : taux normal local, facturé par nous. */
  DOM_LOCAL_VAT = 'DOM_LOCAL_VAT',
  /** DOM, TVA autoliquidée par le pro preneur : on facture HT. */
  DOM_REVERSE_CHARGE = 'DOM_REVERSE_CHARGE',
  /** Guyane / Mayotte : TVA provisoirement non applicable (art. 294-1 du CGI). */
  EXEMPT_CGI_294 = 'EXEMPT_CGI_294',
  /** Autre État membre de l'UE, preneur assujetti : autoliquidation intracommunautaire. */
  EU_REVERSE_CHARGE = 'EU_REVERSE_CHARGE',
  /** Preneur établi hors UE : prestation hors du champ de la TVA française. */
  OUT_OF_SCOPE_NON_EU = 'OUT_OF_SCOPE_NON_EU',
}

/**
 * Valeur de régime acceptée en entrée des helpers : l'enum OU sa string brute. Prisma type ses
 * colonnes enum en union de littéraux, pas avec l'enum TS — sans cette largeur, chaque lecture de
 * `professional.vat_regime` demanderait un cast.
 */
export type ProVatRegimeValue = `${ProVatRegime}`;

/**
 * Taux appliqué par régime. `null` = « pas de taux propre » : seul `UNDETERMINED` est dans ce cas,
 * et il fait retomber le calcul sur le taux gelé en base (cf. `resolveVatRatePercent`).
 *
 * 8,5 n'est PAS entier : tout ce qui transporte un taux doit accepter un décimal (colonnes
 * `vat_rate_percent` en Float, jamais les vieilles colonnes `Int`).
 */
export const VAT_REGIME_RATE_PERCENT: Record<ProVatRegime, number | null> = {
  [ProVatRegime.UNDETERMINED]: null,
  [ProVatRegime.FR_METROPOLE]: 20,
  [ProVatRegime.DOM_LOCAL_VAT]: 8.5,
  [ProVatRegime.DOM_REVERSE_CHARGE]: 0,
  [ProVatRegime.EXEMPT_CGI_294]: 0,
  [ProVatRegime.EU_REVERSE_CHARGE]: 0,
  [ProVatRegime.OUT_OF_SCOPE_NON_EU]: 0,
};

/**
 * Codes pays (ISO 3166-1 alpha-2) relevant de la TVA métropolitaine au taux normal.
 *
 * Monaco en fait partie : la convention fiscale franco-monégasque du 18 mai 1963 place la
 * principauté dans le territoire TVA français, aux mêmes taux. Sans lui, `MC` tombait dans le
 * défaut `OUT_OF_SCOPE_NON_EU` et l'admin se voyait suggérer 0 % « hors champ » — une facture
 * définitive à 0 % là où 20 % sont dus.
 */
export const FR_METROPOLE_COUNTRY_CODES: readonly string[] = ['FR', 'MC'];

/**
 * Sous-ensemble que le code s'autorise à affecter SEUL (cf. `resolveAutoAssignableVatRegime`).
 * Volontairement limité à `FR`, à l'identique du backfill SQL de la migration
 * `add_pro_vat_regime` : un pro monégasque passe par l'arbitrage admin, où la suggestion
 * FR_METROPOLE ci-dessus l'attend déjà. Élargir cette liste sans toucher au backfill créerait
 * deux vérités sur le même pays.
 */
const AUTO_ASSIGNABLE_COUNTRY_CODES: readonly string[] = ['FR'];

/** DOM où la TVA locale s'applique (taux normal 8,5 %). */
export const DOM_LOCAL_VAT_COUNTRY_CODES: readonly string[] = ['GP', 'MQ', 'RE'];

/** Territoires où la TVA n'est provisoirement pas applicable (art. 294-1 du CGI). */
export const NO_VAT_CGI_294_COUNTRY_CODES: readonly string[] = ['GF', 'YT'];

/** États membres de l'UE hors France (autoliquidation B2B possible). */
export const EU_COUNTRY_CODES: readonly string[] = [
  'AT',
  'BE',
  'BG',
  'CY',
  'CZ',
  'DE',
  'DK',
  'EE',
  'ES',
  'FI',
  'GR',
  'HR',
  'HU',
  'IE',
  'IT',
  'LT',
  'LU',
  'LV',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SE',
  'SI',
  'SK',
];

const normalizeCountryCode = (country_code: string | null | undefined): string | null => {
  if (country_code === null || country_code === undefined) return null;
  const trimmed = country_code.trim().toUpperCase();
  return trimmed === '' ? null : trimmed;
};

/**
 * Régime SUGGÉRÉ à partir du pays d'adresse. C'est une aide à la décision affichée en admin,
 * jamais une affectation automatique (sauf métropole, cf. `resolveAutoAssignableVatRegime`) :
 * le pays ne dit pas si le preneur autoliquide.
 *
 * `null` = pas d'adresse exploitable, aucune suggestion à afficher.
 */
export const suggestVatRegimeFromCountry = (country_code: string | null | undefined): ProVatRegime | null => {
  const code = normalizeCountryCode(country_code);
  if (code === null) return null;
  if (FR_METROPOLE_COUNTRY_CODES.includes(code)) return ProVatRegime.FR_METROPOLE;
  if (DOM_LOCAL_VAT_COUNTRY_CODES.includes(code)) return ProVatRegime.DOM_LOCAL_VAT;
  if (NO_VAT_CGI_294_COUNTRY_CODES.includes(code)) return ProVatRegime.EXEMPT_CGI_294;
  if (EU_COUNTRY_CODES.includes(code)) return ProVatRegime.EU_REVERSE_CHARGE;
  return ProVatRegime.OUT_OF_SCOPE_NON_EU;
};

/**
 * Seul cas où le code s'autorise à décider seul : la métropole, où il n'existe qu'un traitement
 * possible. Tout le reste retourne `null` et part en arbitrage manuel — une facture est définitive
 * et non supprimable, on ne devine pas un régime d'exonération.
 */
export const resolveAutoAssignableVatRegime = (country_code: string | null | undefined): ProVatRegime | null => {
  const code = normalizeCountryCode(country_code);
  if (code === null) return null;
  return AUTO_ASSIGNABLE_COUNTRY_CODES.includes(code) ? ProVatRegime.FR_METROPOLE : null;
};

/**
 * Taux effectif : celui du régime, ou le taux gelé en base tant que le régime n'est pas tranché.
 * `frozen_rate_percent` est la dernière valeur connue (colonne héritée) — on ne la réécrit jamais
 * tout seul, elle sert à ne pas changer le montant d'un pro sous nos pieds.
 */
export const resolveVatRatePercent = (regime: ProVatRegimeValue, frozen_rate_percent: number): number =>
  VAT_REGIME_RATE_PERCENT[regime] ?? frozen_rate_percent;

/** true = régime pas encore tranché : le pro remonte dans la liste d'arbitrage admin. */
export const isVatRegimeReviewRequired = (regime: ProVatRegimeValue): boolean => regime === ProVatRegime.UNDETERMINED;

/**
 * Régimes qui exigent le n° de TVA intracommunautaire du PRENEUR sur la facture (art. 289 II du
 * CGI) : sans lui, l'autoliquidation n'est pas opposable et la facture est irrégulière.
 *
 * Vérifié en sandbox Pennylane le 2026-08-30 : le PDF n'imprime « N° de TVA … » que si la fiche
 * client en porte un, et l'API accepte sans broncher une facture `exempt` sans ce numéro — rien
 * ne nous arrêtera côté fournisseur, la garde doit être ici.
 */
export const VAT_REGIMES_REQUIRING_BUYER_VAT_NUMBER: readonly ProVatRegime[] = [ProVatRegime.EU_REVERSE_CHARGE];

export const isBuyerVatNumberRequired = (regime: ProVatRegimeValue): boolean =>
  (VAT_REGIMES_REQUIRING_BUYER_VAT_NUMBER as readonly string[]).includes(regime);

/**
 * Préfixes autorisés en tête d'un n° de TVA intracommunautaire. Ce ne sont PAS les codes ISO des
 * pays : la Grèce utilise `EL` (et non `GR`), et l'Irlande du Nord `XI` depuis le Brexit.
 * `FR` s'ajoute à `EU_COUNTRY_CODES`, qui liste volontairement l'UE hors France.
 */
const INTRA_EU_VAT_PREFIXES: readonly string[] = [
  ...EU_COUNTRY_CODES.filter((code): boolean => code !== 'GR'),
  'FR',
  'EL',
  'XI',
];

/**
 * Format d'un n° de TVA intracommunautaire, une fois les séparateurs retirés : préfixe pays puis
 * 2 à 13 caractères alphanumériques (certains États membres en utilisent : `NL…B01`, `IE…FA`).
 * C'est ce que Pennylane accepte sur une fiche client (« 2 symbols country code followed by 2-13
 * digits ») — le champ de saisie est libre, donc la seule façon de ne pas se faire rejeter en 422
 * est de vérifier ici.
 */
const INTRA_EU_VAT_NUMBER_REGEX = /^([A-Z]{2})([0-9A-Z]{2,13})$/;

/**
 * Bornes sur le corps du numéro (après le préfixe pays). Aucun format de l'UE ne porte plus de
 * 2 lettres (`FR` : clé alphabétique, `IE…FA`, `ES` : lettre en tête et en fin) ni moins de
 * 2 chiffres (`RO` : 2 à 10 chiffres). Sans ces bornes, la forme seule laisse passer des MOTS dont
 * les deux premières lettres tombent sur un préfixe valide : « demande en cours » (`DE`),
 * « attente » (`AT`), « Plus tard » (`PL`), « SIRET 123456789 » (`SI`).
 */
const INTRA_EU_VAT_BODY_MAX_LETTERS = 2;
const INTRA_EU_VAT_BODY_MIN_DIGITS = 2;

/**
 * Même règle que `normalizeIntraEuVatNumber`, exprimée en `pattern` JSON Schema pour les champs de
 * saisie (profil pro, infos de facturation) : le refus arrive À LA SAISIE, avec un message, plutôt
 * qu'à l'émission de la facture. Un `pattern` n'a pas de drapeau `i` ni d'étape de nettoyage, donc
 * la casse et les séparateurs (espaces, points, tirets) sont tolérés dans l'expression elle-même.
 * La chaîne vide reste valide : c'est ainsi que le formulaire efface le numéro.
 * `vat.spec.ts` vérifie que ce pattern et le normaliseur tranchent pareil sur la FORME. La clé d'un
 * n° FR (un modulo, hors de portée d'une regex) n'est contrôlée que par le normaliseur — d'où
 * `isVatNumberInputRejected` côté services.
 */
export const intraEuVatNumberPattern = ((): string => {
  const prefixes = INTRA_EU_VAT_PREFIXES.map(
    (p): string => `[${p[0]}${p[0].toLowerCase()}][${p[1]}${p[1].toLowerCase()}]`,
  ).join('|');
  const maxLetters = `(?!(?:[^A-Za-z]*[A-Za-z]){${INTRA_EU_VAT_BODY_MAX_LETTERS + 1}})`;
  const minDigits = `(?=(?:[^0-9]*[0-9]){${INTRA_EU_VAT_BODY_MIN_DIGITS}})`;
  return `^\\s*(?:(?:${prefixes})${maxLetters}${minDigits}(?:[\\s.-]*[0-9A-Za-z]){2,13}[\\s.-]*)?$`;
})();

/**
 * N° de TVA français à clé numérique : `FR` + clé (2 chiffres) + SIREN (9 chiffres). La clé est
 * calculée depuis le SIREN — `(12 + 3 × (SIREN mod 97)) mod 97` — donc une faute de frappe dans
 * l'un ou l'autre se voit sans interroger VIES. Pennylane, lui, l'interroge : un n° bien formé mais
 * à la clé fausse lui est « unregistered », il refuse la fiche client (422) et aucune facture ne
 * sort. Les clés alphanumériques (`FRAB…`, attribuées hors SIREN) n'ont pas de formule publique :
 * elles ne sont pas contrôlées ici.
 */
const FR_NUMERIC_KEY_VAT_REGEX = /^FR([0-9]{2})([0-9]{9})$/;

const hasValidFrenchVatKey = (compact: string): boolean => {
  const match = FR_NUMERIC_KEY_VAT_REGEX.exec(compact);
  if (match === null) return true;
  const siren = Number(match[2]);
  return Number(match[1]) === (12 + 3 * (siren % 97)) % 97;
};

/**
 * Normalise un n° de TVA saisi à la main : espaces / points / tirets retirés, mis en majuscules.
 * Rend `null` dès que le résultat ne ressemble pas à un n° intracommunautaire — un champ libre
 * reçoit aussi bien « non assujetti » qu'un identifiant fiscal hors UE, et transmettre ça au
 * fournisseur de facturation fait échouer TOUTE la facture (Pennylane répond 422 sur la création
 * de la fiche client, donc aucun document n'est produit et le cron de réconciliation rejoue
 * l'échec chaque nuit).
 *
 * Pour les régimes qui exigent ce numéro (`isBuyerVatNumberRequired`), un `null` doit être traité
 * comme une absence et bloquer l'émission : mieux vaut une facture retardée qu'une facture
 * définitive sans la mention obligatoire.
 */
export const normalizeIntraEuVatNumber = (raw: string | null | undefined): string | null => {
  if (!raw) return null;
  const compact = raw.replace(/[\s.-]/g, '').toUpperCase();
  const match = INTRA_EU_VAT_NUMBER_REGEX.exec(compact);
  // Le préfixe pays est contrôlé en plus de la forme : sans lui, un identifiant fiscal hors UE
  // collé au format (« CHE-123.456.789 TVA ») passerait pour un n° intracommunautaire.
  if (match === null || !INTRA_EU_VAT_PREFIXES.includes(match[1])) return null;
  const digits = match[2].replace(/[^0-9]/g, '').length;
  const letters = match[2].length - digits;
  if (letters > INTRA_EU_VAT_BODY_MAX_LETTERS || digits < INTRA_EU_VAT_BODY_MIN_DIGITS) return null;
  // Contrôle que le `pattern` de saisie ne peut pas exprimer (un modulo) : les services qui
  // enregistrent le n° le refusent donc eux-mêmes (`isVatNumberInputRejected`).
  if (!hasValidFrenchVatKey(compact)) return null;
  return compact;
};

/**
 * `true` = saisie non vide que `normalizeIntraEuVatNumber` rejette. Le `pattern` du schéma refuse
 * déjà les formats faux, mais pas une clé FR fausse : sans ce contrôle à l'enregistrement, le n°
 * serait accepté puis écarté en silence à l'émission de la facture. Chaîne vide = effacement.
 */
export const isVatNumberInputRejected = (raw: string | null | undefined): boolean =>
  !!raw?.trim() && normalizeIntraEuVatNumber(raw) === null;

/**
 * Source minimale pour retrouver le pays d'un pro. Les adresses sont des colonnes `Json` côté
 * Prisma, donc `unknown` : ce helper les sonde défensivement plutôt que d'exiger un cast.
 */
export interface ProCountrySource {
  /** Colonne scalaire `professional.country` — toujours renseignée, ISO alpha-2. */
  country?: string | null;
  address?: unknown;
  billing_address?: unknown;
}

const ALPHA_2 = /^[A-Z]{2}$/;

const readAddressCountryCode = (address: unknown): string | null => {
  if (address === null || typeof address !== 'object') return null;
  const obj = address as { country_code?: unknown; country?: unknown };
  const explicit = normalizeCountryCode(typeof obj.country_code === 'string' ? obj.country_code : null);
  if (explicit !== null) return explicit;
  // `address.country` est un champ LIBELLÉ : il contient « France » chez les uns, « RE » chez les
  // autres (import v1). On ne retient que ce qui est déjà un code alpha-2 — un libellé traduit
  // n'est pas mappable de façon fiable et doit laisser la main au fallback suivant.
  const label = normalizeCountryCode(typeof obj.country === 'string' ? obj.country : null);
  return label !== null && ALPHA_2.test(label) ? label : null;
};

/**
 * Pays de facturation du pro, en ISO alpha-2. Lire `address.country_code` seul est un bug :
 * seuls ~45 % des pros en portent un (mesuré en prod le 2026-08-30), les autres n'ont que le
 * libellé `address.country` ou la colonne scalaire `country`. Un pro réunionnais dont l'adresse
 * n'a pas de `country_code` était vu comme métropolitain — donc facturé à 20 % au lieu d'être
 * arbitré.
 *
 * Ordre : adresse de facturation > adresse principale > colonne scalaire (la plus ancienne, donc
 * potentiellement périmée si le pro a déménagé sans qu'on la mette à jour).
 */
export const resolveProCountryCode = (pro: ProCountrySource): string | null => {
  const fromAddress = readAddressCountryCode(pro.billing_address) ?? readAddressCountryCode(pro.address);
  if (fromAddress !== null) return fromAddress;
  const scalar = normalizeCountryCode(pro.country);
  return scalar !== null && ALPHA_2.test(scalar) ? scalar : null;
};

/**
 * Pays d'EXERCICE du pro — à ne pas confondre avec `resolveProCountryCode`,
 * qui part de l'adresse de FACTURATION parce que c'est ce que la TVA exige.
 *
 * Ici la question est tout autre : « dans quel pays sont les clients de ce
 * pro ? ». Elle sert à interpréter un téléphone écrit en format NATIONAL
 * (« 06 12 34 56 78 »), le seul cas où le numéro ne porte pas son pays — une
 * forme internationale (`+33…`) se parse sans aucune région. Un salon qui
 * exerce en France mais facture au Luxembourg verrait sinon tous ses numéros
 * nationaux rejetés.
 *
 * Ordre : adresse d'exercice > colonne pays > adresse de facturation en dernier
 * recours (la colonne étant non nulle, ce dernier repli est théorique).
 */
export const resolveProOperatingCountryCode = (pro: ProCountrySource): string | null => {
  const fromAddress = readAddressCountryCode(pro.address);
  if (fromAddress !== null) return fromAddress;
  const scalar = normalizeCountryCode(pro.country);
  if (scalar !== null && ALPHA_2.test(scalar)) return scalar;
  return readAddressCountryCode(pro.billing_address);
};
